import React, { useMemo, useState } from 'react'
import { useQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { Check, FilePlus2, FileSearch, FileX, FolderOpen, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import FolderTree, { type FolderTreeNode } from '@/components/doc-control/FolderTree'
import * as Query from '../../../api/gql/query'
import FileTypeIcon from '../../../components/file-manager/FileIcon'
import { formatBytes } from '../../../utils/formatters'
import { folderSelectionState, setFolderDocuments, toggleDocument } from '../utils/document-selection'
import DocumentSelectorModalSkeleton, { DocumentSelectorListSkeleton } from './DocumentSelectorModalSkeleton'

interface Document {
  id: string
  name: string
  currentRevision?: {
    id: string
    version: number
    downloadUrl?: string
    file?: {
      id: string
      filename: string
      size: number
      mimetype: string
    }
  }
}

interface DocumentSelectorModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (documents: Document[]) => void
  excludeDocumentIds?: string[]
  loading?: boolean
  projectId: string
  /**
   * false: pick exactly one document — radio rows and a "Select" button;
   * `onSelect` then always receives a single-item array. Defaults to true.
   */
  multiple?: boolean
}

const PANE_TITLE = 'text-xs font-medium uppercase tracking-wide text-muted-foreground'
// The table's own overflow wrapper (the box's only child) becomes the scroll
// area, so the sticky header stays put while the rows scroll.
const LIST_BOX = 'flex h-72 flex-col gap-0 overflow-hidden rounded-md border md:h-[400px] [&>div]:min-h-0 [&>div]:flex-1'

const DocumentSelectorModal: React.FC<DocumentSelectorModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  excludeDocumentIds = [],
  loading: externalLoading = false,
  projectId,
  multiple = true,
}) => {
  const { t } = useTranslation()
  // Folder and open branches live here, outside the dialog content, so they
  // survive closing and reopening the picker from the same page.
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(() => new Set())
  const [selection, setSelection] = useState<Document[]>([])

  const { data: folderData, loading: foldersLoading } = useQuery(Query.DOC_FOLDER_TREE, {
    variables: { projectId },
    skip: !projectId,
  })

  const { data: documentsData, loading: documentsLoading } = useQuery(Query.DOC_DOCUMENTS, {
    variables: { folderId: selectedFolderId },
    skip: !selectedFolderId,
    fetchPolicy: 'network-only',
  })

  const folders: FolderTreeNode[] = folderData?.docFolderTree || []
  const documents: Document[] = documentsData?.docDocuments || []

  const availableDocuments = useMemo(() => {
    const excluded = new Set(excludeDocumentIds)
    return documents.filter((doc) => !excluded.has(doc.id))
  }, [documents, excludeDocumentIds])
  const selectedIds = useMemo(() => new Set(selection.map((doc) => doc.id)), [selection])

  const pick = (doc: Document) => setSelection((prev) => toggleDocument(prev, doc, multiple))

  const finish = (picked: Document[] | null) => {
    if (picked) onSelect(picked)
    setSelection([])
    onClose()
  }
  const handleClose = () => finish(null)
  const handleConfirm = () => {
    if (selection.length > 0) finish(selection)
  }

  const isLoading = foldersLoading || externalLoading

  const emptyState = (Icon: React.ElementType, text: string) => (
    <div className={LIST_BOX}>
      <div className="flex flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
        <Icon className="h-8 w-8 opacity-40" />
        {text}
      </div>
    </div>
  )

  const renderDocuments = () => {
    if (!selectedFolderId) return emptyState(FolderOpen, t('DocControl_select_folder_first'))
    if (documentsLoading) {
      return (
        <div className={LIST_BOX}>
          <DocumentSelectorListSkeleton />
        </div>
      )
    }
    if (availableDocuments.length === 0) {
      return emptyState(
        FileX,
        documents.length === 0 ? t('DocControl_no_documents_in_folder') : t('DocControl_all_documents_already_added'),
      )
    }

    const table = (
      <Table className="table-fixed">
        <TableHeader className="sticky top-0 z-10 bg-background">
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 w-10 pl-3 pr-0">
              {multiple && (
                <Checkbox
                  checked={folderSelectionState(selection, availableDocuments)}
                  onCheckedChange={(checked) => setSelection((prev) => setFolderDocuments(prev, availableDocuments, checked === true))}
                  aria-label={t('Common_select_all')}
                />
              )}
            </TableHead>
            <TableHead className="h-9">{t('DocControl_document')}</TableHead>
            <TableHead className="h-9 w-20">{t('DocControl_version')}</TableHead>
            <TableHead className="h-9 w-24 text-right">{t('DocControl_size')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {availableDocuments.map((doc) => {
            const selected = selectedIds.has(doc.id)
            const size = doc.currentRevision?.file?.size
            return (
              <TableRow
                key={doc.id}
                data-state={selected ? 'selected' : undefined}
                className="cursor-pointer"
                onClick={() => pick(doc)}
                onDoubleClick={multiple ? undefined : () => finish([doc])}
              >
                <TableCell className="w-10 py-2 pl-3 pr-0" onClick={(e) => e.stopPropagation()}>
                  {multiple ? (
                    <Checkbox checked={selected} onCheckedChange={() => pick(doc)} aria-label={doc.name} />
                  ) : (
                    <RadioGroupItem value={doc.id} aria-label={doc.name} />
                  )}
                </TableCell>
                <TableCell className="py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <FileTypeIcon filename={doc.currentRevision?.file?.filename ?? doc.name} size={18} />
                    <span className="truncate" title={doc.name}>
                      {doc.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-2">
                  {doc.currentRevision && (
                    <Badge variant="secondary" className="whitespace-nowrap tabular-nums">
                      v{doc.currentRevision.version}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="py-2 text-right tabular-nums text-muted-foreground">{size ? formatBytes(size) : '-'}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    )

    if (multiple) return <div className={LIST_BOX}>{table}</div>
    return (
      <RadioGroup
        value={selection[0]?.id ?? ''}
        onValueChange={(id) => {
          const doc = availableDocuments.find((d) => d.id === id)
          if (doc) pick(doc)
        }}
        className={LIST_BOX}
      >
        {table}
      </RadioGroup>
    )
  }

  const TitleIcon = multiple ? FilePlus2 : FileSearch

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="sm:max-w-4xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TitleIcon className="h-5 w-5 text-primary" />
            {multiple ? t('DocControl_select_documents_to_add') : t('DocControl_select_document')}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <DocumentSelectorModalSkeleton />
        ) : (
          <div className="grid gap-4 md:grid-cols-[20rem_minmax(0,1fr)]">
            <section className="flex min-w-0 flex-col gap-2">
              <h3 className={PANE_TITLE}>{t('DocControl_folders')}</h3>
              <div className="h-48 overflow-y-auto rounded-md border p-1.5 md:h-[400px]">
                <FolderTree
                  folders={folders}
                  selectedId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                  expanded={expandedFolders}
                  onExpandedChange={setExpandedFolders}
                />
              </div>
            </section>
            <section className="flex min-w-0 flex-col gap-2">
              <h3 className={PANE_TITLE}>{t('DocControl_documents')}</h3>
              {renderDocuments()}
            </section>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t pt-4">
          {multiple && selection.length > 0 && (
            <>
              <span className="text-sm text-muted-foreground">{t('DocControl_selected_count', { count: selection.length })}</span>
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setSelection([])}>
                {t('Common_deselect_all')}
              </Button>
            </>
          )}
          {!multiple && selection.length > 0 && (
            <span className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
              <Check className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{selection[0].name}</span>
            </span>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={handleClose}>
            {t('Common_cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={selection.length === 0}>
            {multiple ? <Plus className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            {multiple ? t('DocControl_add_selected', { count: selection.length }) : t('Common_select')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DocumentSelectorModal
